/**
 * İnce motor: dinamik iz sürme.
 *
 * Giderek daralan, zikzak çizen bir koridor. Çocuk kalemi
 * kaldırmadan, kenarlara değmeden sonuna gidiyor.
 *
 * ÜRETİMİN ASIL İŞİ KORİDORUN GEÇERLİ OLMASI. İki kenar, orta
 * çizginin sağına ve soluna kaydırılarak çiziliyor; keskin bir
 * dönüşte iç kenar kendi üstüne katlanıyor ve ortaya geçilemeyen bir
 * düğüm çıkıyor. Sayfaya bakınca fark edilmiyor — koridor karışık
 * zaten — ama çocuk orada takılıp kalıyor ve bunu kendi
 * beceriksizliğine yoruyor. Üretim her dönüşte katlanmayı denetliyor
 * ve katlanan kurguyu atıyor.
 */

import { Rng } from '../../prng'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult } from '../types'

type Nokta = [number, number]

/* ================================================================== *
 * 40. Dinamik iz sürme
 *
 * KÂĞIT VE EKRAN. Kâğıtta kalemle, ekranda parmakla. Ekran kanalında
 * puanlama örneklenen noktaların koridorun içinde kalıp kalmadığına
 * bakıyor; kâğıtta uygulayıcı bakıyor.
 *
 * Ekran puanlaması "kaç kez değdi" DEMİYOR, "değdi mi" diyor.
 * Dokunma örnekleme sıklığı cihazdan cihaza değişiyor: hızlı bir
 * tablette aynı hata on örnek, yavaşında üç örnek üretiyor ve sayı
 * çocuğun değil cihazın özelliği oluyor.
 * ================================================================== */
export const trackPath: ExerciseTemplate = {
  code: 'track_path',
  title: 'Yoldan çıkma',
  domains: ['processing_speed', 'selective_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç dönüş */
      donus: scale(l, 4, 10),
      /**
       * Koridorun başlangıç yarı genişliği (0–1 kutusunda).
       *
       * TABANI VAR ve bedava değil: ilk hali 10. düzeyde koridoru
       * 0.0103 yarı genişliğe indiriyordu. Çizim alanı kâğıtta yaklaşık
       * 95 mm; bu, 2 mm'lik bir koridor demek ve kalem çizgisi
       * 0,5 mm. Çocuğun yapabileceği bir iş değil — ince motor
       * çalışması değil, imkânsız bir görev. En dar hali tam genişlik
       * olarak 4 mm'nin altına inmiyor.
       */
      genislik: Number((0.063 - l * 0.0031).toFixed(4)),
      /** Sona doğru ne kadar daralıyor (oran) */
      daralma: Number((0.88 - l * 0.028).toFixed(3)),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const donus = p['donus'] as number
    const bas = p['genislik'] as number
    const daralma = p['daralma'] as number

    let kurgu: { orta: Nokta[]; sol: Nokta[]; sag: Nokta[]; yari: number[] } | null = null
    for (let deneme = 0; deneme < 80 && !kurgu; deneme++) {
      const rng = new Rng(Number(seed) + deneme * 7919)
      const orta = ortaCizgi(rng, donus)
      const yari = orta.map((_, i) => bas * (1 - (1 - daralma) * (i / (orta.length - 1))))
      const kenarlar = kenarCikar(orta, yari)
      if (!kenarlar) continue
      kurgu = { orta, yari, ...kenarlar }
    }
    if (!kurgu) throw new Error('track_path: koridor uretilemedi')

    const { orta, sol, sag, yari } = kurgu
    const basN = orta[0]!
    const sonN = orta[orta.length - 1]!

    return {
      code: 'track_path',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        "A'dan B'ye, iki çizginin arasından, kalemini kaldırmadan git. " +
        'Çizgilere değme. Yol sona doğru daralıyor.',
      spokenPrompt:
        'İki çizginin arasından gideceksin. Kalemini kaldırma ve kenarlara değme; yol gittikçe daralıyor.',
      render: [
        {
          t: 'paths',
          kare: true,
          height: 420,
          items: [
            { points: sol, id: 'sol' },
            { points: sag, id: 'sag' },
          ],
          labels: [
            { x: basN[0], y: basN[1], text: 'A', id: 'a' },
            { x: sonN[0], y: sonN[1], text: 'B', id: 'b' },
          ],
        },
      ],
      solution: { orta, yari, bas: basN, son: sonN },
      grading: 'auto',
      estimatedSeconds: 30 + donus * 6,
      itemCount: 2,
    }
  },
  /**
   * Ekranda çizilen yol: örneklenmiş noktalar.
   *
   * İki şey ayrı ayrı: sona ulaştı mı, ve koridorun içinde kaldı mı.
   * Tek bir doğru/yanlış, "yolu tamamladı ama bir yerde kenara
   * değdi" ile "hiç başlamadı"yı aynı gösteriyordu; ince motor
   * çalışmasında asıl bakılan birincisi.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as {
      orta: Nokta[]
      yari: number[]
      bas: Nokta
      son: Nokta
    }
    const ham = Array.isArray(answer) ? answer : []
    const yol: Nokta[] = ham
      .map((x) => (Array.isArray(x) ? [Number(x[0]), Number(x[1])] : null))
      .filter((x): x is Nokta => x !== null && !Number.isNaN(x[0]) && !Number.isNaN(x[1]))

    const yakin = (a: Nokta, b: Nokta, r: number) => Math.hypot(a[0] - b[0], a[1] - b[1]) <= r
    const basladi = yol.length > 0 && yakin(yol[0]!, sol.bas, 0.08)
    const bitirdi = yol.length > 1 && yakin(yol[yol.length - 1]!, sol.son, 0.08)
    const tamamlandi = basladi && bitirdi

    let disari = 0
    for (const nokta of yol) {
      if (!icindeMi(nokta, sol.orta, sol.yari)) disari++
    }
    const icerde = tamamlandi && disari === 0

    return {
      correct: (tamamlandi ? 1 : 0) + (icerde ? 1 : 0),
      total: 2,
      accuracy: ((tamamlandi ? 1 : 0) + (icerde ? 1 : 0)) / 2,
      perItem: [tamamlandi, icerde],
      items: [
        {
          id: 'tamam',
          beklenen: "A'dan B'ye kesintisiz",
          verilen: yol.length === 0 ? null : tamamlandi ? 'ulaştı' : 'ulaşamadı',
          dogru: tamamlandi,
        },
        {
          id: 'icerde',
          beklenen: 'çizgilere değmeden',
          /*
           * Kaç kez değdiği YAZILMIYOR: dokunma örnekleme sıklığı
           * cihazdan cihaza değişiyor ve o sayı çocuğun değil
           * cihazın özelliği olurdu.
           */
          verilen: yol.length === 0 ? null : disari === 0 ? 'değmedi' : 'değdi',
          dogru: icerde,
        },
      ],
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { orta: Nokta[] }
    return (
      `Kâğıtta anahtar yok: çizginin iki kenara da değmeden A'dan B'ye gitmesi aranıyor. ` +
      `Koridor ${sol.orta.length - 1} bölümden oluşuyor ve sona doğru daralıyor.`
    )
  },
}

/**
 * Orta çizgi: soldan sağa ilerlerken yukarı-aşağı zikzak.
 *
 * Dönüş açısı 60°'yi geçmiyor. Daha keskin dönüşte iç kenar kendi
 * üstüne katlanıyor; sınır yalnızca estetik değil, koridorun
 * geçilebilir kalmasının şartı.
 */
function ortaCizgi(rng: Rng, donus: number): Nokta[] {
  const n = donus + 1
  const out: Nokta[] = []
  for (let i = 0; i <= n; i++) {
    const x = 0.1 + (0.8 * i) / n
    // Zikzak: tek ve çift adımlar ayrı yarıda, genlik rastgele
    const taban = i % 2 === 0 ? 0.3 : 0.7
    const y = i === 0 || i === n ? 0.5 : taban + (rng.float() - 0.5) * 0.18
    out.push([Number(x.toFixed(4)), Number(y.toFixed(4))])
  }
  return out
}

/**
 * İki kenarı orta çizgiden kaydırarak çıkarır.
 *
 * Her köşede iki bölümün ortalama normali kullanılıyor. Kenar
 * bölümünün yönü orta bölümün yönüyle TERS dönerse kenar kendi
 * üstüne katlanmış demektir ve o kurgu atılıyor — bu, sayfaya
 * bakınca görünmeyen ama çocuğu kilitleyen hata.
 */
function kenarCikar(orta: Nokta[], yari: number[]): { sol: Nokta[]; sag: Nokta[] } | null {
  const normaller: Nokta[] = orta.map((_, i) => {
    const onceki = orta[Math.max(0, i - 1)]!
    const sonraki = orta[Math.min(orta.length - 1, i + 1)]!
    const dx = sonraki[0] - onceki[0]
    const dy = sonraki[1] - onceki[1]
    const u = Math.hypot(dx, dy) || 1
    return [-dy / u, dx / u]
  })

  const kaydir = (isaret: number): Nokta[] =>
    orta.map((nk, i) => [
      Number((nk[0] + isaret * normaller[i]![0] * yari[i]!).toFixed(4)),
      Number((nk[1] + isaret * normaller[i]![1] * yari[i]!).toFixed(4)),
    ])

  const sol = kaydir(1)
  const sag = kaydir(-1)

  for (const kenar of [sol, sag]) {
    for (let i = 1; i < kenar.length; i++) {
      const kdx = kenar[i]![0] - kenar[i - 1]![0]
      const kdy = kenar[i]![1] - kenar[i - 1]![1]
      const odx = orta[i]![0] - orta[i - 1]![0]
      const ody = orta[i]![1] - orta[i - 1]![1]
      // Yön ters döndüyse kenar katlanmış
      if (kdx * odx + kdy * ody <= 0) return null
    }
    // Kutunun dışına taşmasın
    for (const [x, y] of kenar) {
      if (x < 0.02 || x > 0.98 || y < 0.02 || y > 0.98) return null
    }
  }
  return { sol, sag }
}

/**
 * Nokta koridorun içinde mi.
 *
 * En yakın orta bölüme olan uzaklık, o noktadaki yarı genişlikten
 * küçükse içerde. Kenar çizgisinin kendisi sınır: tam üstü "değdi"
 * sayılıyor.
 */
function icindeMi(nokta: Nokta, orta: Nokta[], yari: number[]): boolean {
  let enYakin = Number.POSITIVE_INFINITY
  let enYakinYari = yari[0]!
  for (let i = 1; i < orta.length; i++) {
    const a = orta[i - 1]!
    const b = orta[i]!
    const vx = b[0] - a[0]
    const vy = b[1] - a[1]
    const uzunluk2 = vx * vx + vy * vy || 1
    let t = ((nokta[0] - a[0]) * vx + (nokta[1] - a[1]) * vy) / uzunluk2
    t = Math.min(1, Math.max(0, t))
    const px = a[0] + t * vx
    const py = a[1] + t * vy
    const d = Math.hypot(nokta[0] - px, nokta[1] - py)
    if (d < enYakin) {
      enYakin = d
      enYakinYari = yari[i - 1]! + t * (yari[i]! - yari[i - 1]!)
    }
  }
  return enYakin < enYakinYari
}

export const _ic = { icindeMi, kenarCikar, ortaCizgi }

/* ================================================================== *
 * 41. Ritmi tekrarla
 *
 * Bir ritim çalınıyor; çocuk aynı ritmi dokunarak tekrarlıyor.
 *
 * ÖLÇÜM BATARYASINA EKLENMEDİ, egzersiz olarak duruyor. `nrg-core`
 * §4 gereği donduruldu: yeni bir görev eklemek ölçümün yapısını ve
 * uzunluğunu değiştirir, `INSTRUMENT_VERSION` yükselir ve o andan
 * sonra alınan sayılar eskileriyle karşılaştırılamaz. Önce–sonra
 * karşılaştırması ürünün dayanağı; bir egzersiz eklemek için
 * feda edilmez. Egzersiz puanı ise klinik bir ölçüt değil, çalışma
 * kaydı — orada serbest.
 *
 * PUANLAMA ÜÇ PARÇALI ve ortadaki asıl olan:
 *   1. vuruş sayısı — ritmi kaç parçaya böldü
 *   2. ORAN — aralıkların birbirine göre oranı doğru mu
 *   3. tempo — genel hız yakın mı
 *
 * Mutlak milisaniyeye bakan tek bir puan, ritmi doğru ama yavaş
 * tekrarlayan çocuğu sıfırlıyordu. Oysa "kısa kısa uzun" dizisini
 * yavaş çalmak ritmi bilmek demek; tempo ayrı bir beceri ve ayrı
 * yazılıyor.
 * ================================================================== */
export const rhythmTap: ExerciseTemplate = {
  code: 'rhythm_tap',
  title: 'Ritmi tekrarla',
  domains: ['processing_speed', 'working_memory_visual'],
  channels: ['digital'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç vuruş */
      vurus: scale(l, 4, 8),
      /** Kaç farklı süre değeri: ikisi 1:2, üçü 1:2:3 */
      deger: l >= 5 ? 3 : 2,
      /** Temel birim (ms) */
      birim: 400,
      /** Oran hatası bu oranı geçmesin */
      tolerans: Number((0.3 - l * 0.015).toFixed(3)),
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const vurus = p['vurus'] as number
    const birim = p['birim'] as number
    const degerler = p['deger'] === 3 ? [1, 2, 3] : [1, 2]

    /**
     * Desen tekdüze OLMAMALI.
     *
     * Bütün aralıklar aynıysa görev ritim tekrarı değil, eşit
     * aralıklarla dokunma oluyor: tempoyu ölçer, ritmi ölçmez.
     */
    let katlar: number[] = []
    for (let deneme = 0; deneme < 60; deneme++) {
      katlar = Array.from({ length: vurus - 1 }, () => rng.pick(degerler))
      if (new Set(katlar).size > 1) break
    }
    if (new Set(katlar).size < 2) katlar[0] = degerler[degerler.length - 1]!

    const araliklar = katlar.map((k) => k * birim)

    return {
      code: 'rhythm_tap',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Ritmi dinle, sonra aynı ritimde ekrana dokun. Aynı hızda olmak zorunda değilsin; ' +
        'vuruşlar arasındaki oran aynı olsun.',
      spokenPrompt:
        'Önce ritmi dinleyeceksin, sonra aynısını parmağınla tekrarlayacaksın.',
      render: [{ t: 'rhythm', araliklar }],
      solution: { araliklar, tolerans: p['tolerans'] as number },
      grading: 'auto',
      // Dinleme + tekrar, birkaç kez: toplam süre kabaca ritmin üç katı
      estimatedSeconds: Math.ceil((araliklar.reduce((a, b) => a + b, 0) / 1000) * 3) + 25,
      itemCount: 3,
    }
  },
  /**
   * Cevap: vuruş zamanları (ms), ilk vuruş sıfır kabul edilerek.
   *
   * Saatin başlangıcı değil ARALIKLAR önemli; mutlak zaman damgası
   * göndermek cihaz saatine bağımlılık yaratırdı.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { araliklar: number[]; tolerans: number }
    const zamanlar = (Array.isArray(answer) ? answer : [])
      .map(Number)
      .filter((x) => Number.isFinite(x))
    const verilen: number[] = []
    for (let i = 1; i < zamanlar.length; i++) verilen.push(zamanlar[i]! - zamanlar[i - 1]!)

    const sayiDogru = verilen.length === sol.araliklar.length && verilen.every((d) => d > 0)

    let oranDogru = false
    let tempoDogru = false
    let enKotu: number | null = null
    let tempoOrani: number | null = null
    if (sayiDogru) {
      const ortB = sol.araliklar.reduce((a, b) => a + b, 0) / sol.araliklar.length
      const ortV = verilen.reduce((a, b) => a + b, 0) / verilen.length
      /*
       * Aralıklar kendi ortalamalarına bölünerek karşılaştırılıyor:
       * ritmi doğru ama yavaş tekrarlamak ritmi bilmek demek.
       */
      enKotu = Math.max(
        ...sol.araliklar.map((b, i) => Math.abs(verilen[i]! / ortV - b / ortB) / (b / ortB)),
      )
      oranDogru = enKotu <= sol.tolerans
      tempoOrani = ortV / ortB
      tempoDogru = tempoOrani >= 0.6 && tempoOrani <= 1.6
    }

    const items: ItemResult[] = [
      {
        id: 'sayi',
        beklenen: `${sol.araliklar.length + 1} vuruş`,
        verilen: zamanlar.length === 0 ? null : `${zamanlar.length} vuruş`,
        dogru: sayiDogru,
      },
      {
        id: 'oran',
        beklenen: `aralık oranları (en fazla %${Math.round(sol.tolerans * 100)} sapma)`,
        verilen: enKotu === null ? null : `en büyük sapma %${Math.round(enKotu * 100)}`,
        dogru: oranDogru,
      },
      {
        id: 'tempo',
        beklenen: 'genel hız yakın',
        verilen: tempoOrani === null ? null : `${tempoOrani.toFixed(2)} kat`,
        dogru: tempoDogru,
      },
    ]
    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: items.length,
      accuracy: dogru / items.length,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { araliklar: number[] }
    return `Ekran çalışması. Ritim (ms): ${sol.araliklar.join(' - ')}`
  },
}
